import { useState, useRef, useEffect, type ChangeEvent, type FormEvent, type DragEvent } from 'react';
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  X,
  File,
  Plus,
  Trash2,
} from 'lucide-react';
import { isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import { Drawer, useToast } from './ui';
import Confirm from './Confirm';
import {
  DRAWING_CATEGORIES,
  CATEGORY_ORDER,
  type DrawingCategory,
  type ProjectDrawingRow,
  type NewDrawingPayload,
  useUploadMultipleDrawings,
  useDeleteDrawing,
  useProjectDrawings,
  formatFileSize,
} from '../lib/project-drawings';

interface Props {
  projectId: string;
  projectName?: string;
  initialCategory?: DrawingCategory;
  onClose: () => void;
  onSuccess?: () => void;
}

interface StagedDrawingFile {
  id: string;
  category: DrawingCategory;
  file: File;
  previewUrl: string;
  title: string;
  description: string;
}

export default function DrawingUploadDrawer({
  projectId,
  projectName,
  initialCategory = 'floor_plan',
  onClose,
  onSuccess,
}: Props) {
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [category, setCategory] = useState<DrawingCategory>(initialCategory);
  const [stagedFiles, setStagedFiles] = useState<StagedDrawingFile[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<ProjectDrawingRow | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const deleteMutation = useDeleteDrawing(projectId);

  // Sync category when initialCategory changes
  useEffect(() => {
    if (initialCategory) {
      setCategory(initialCategory);
    }
  }, [initialCategory]);

  const { data: projectDrawings } = useProjectDrawings(projectId);
  const batchUploadMutation = useUploadMultipleDrawings(projectId);

  const currentCategoryFiles = stagedFiles.filter((f) => f.category === category);
  const activeCategoryMeta = DRAWING_CATEGORIES[category] || DRAWING_CATEGORIES['floor_plan'];
  const currentLatest = projectDrawings?.latest[category];

  const getStagedCount = (catKey: DrawingCategory) =>
    stagedFiles.filter((f) => f.category === catKey).length;

  const totalStagedCount = stagedFiles.length;

  function processFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    fileArray.forEach((file) => {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      const reader = new FileReader();
      reader.onload = () => {
        const previewUrl = reader.result as string;
        setStagedFiles((prev) => [
          ...prev,
          {
            id: `${category}-${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            category,
            file,
            previewUrl,
            title: cleanName || `${activeCategoryMeta.label} - ${file.name}`,
            description: '',
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    processFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  }

  function removeStagedFile(id: string) {
    setStagedFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function updateStagedTitle(id: string, newTitle: string) {
    setStagedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, title: newTitle } : f)),
    );
  }

  function updateStagedDescription(id: string, newDesc: string) {
    setStagedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, description: newDesc } : f)),
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (stagedFiles.length === 0) {
      toast.show('Please select at least one drawing file to upload', true);
      return;
    }

    setLoading(true);
    try {
      const payloads: NewDrawingPayload[] = stagedFiles.map((staged) => ({
        projectId,
        category: staged.category,
        title: staged.title.trim() || staged.file.name,
        fileName: staged.file.name,
        fileType: staged.file.type || 'application/octet-stream',
        fileSize: staged.file.size,
        fileUrl: staged.previewUrl,
        description: staged.description.trim() || undefined,
      }));

      await batchUploadMutation.mutateAsync(payloads);

      const categoryLabels = Array.from(
        new Set(stagedFiles.map((s) => DRAWING_CATEGORIES[s.category]?.label || s.category)),
      ).join(', ');

      toast.show(
        stagedFiles.length === 1
          ? `Drawing file uploaded successfully (${categoryLabels})`
          : `${stagedFiles.length} drawing files uploaded successfully (${categoryLabels})`,
      );

      onSuccess?.();
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Failed to upload drawings', true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Drawer
      title="Upload Project Drawings"
      description={
        projectName
          ? `Add drawings and plans for ${projectName}`
          : 'Upload floor plans, structural details, working drawings, and 3D elevation'
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={
        totalStagedCount === 0
          ? 'Upload Drawings'
          : totalStagedCount === 1
          ? 'Upload 1 Drawing File'
          : `Upload ${totalStagedCount} Drawing Files`
      }
      busy={loading || batchUploadMutation.isPending}
      wide
    >
      {/* 1. Category Selection Tabs */}
      <div style={{ marginBottom: 20 }}>
        <label className="field-label" style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
          Select Drawing Category:
        </label>
        <div
          className="drawing-cat-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
            gap: 10,
          }}
        >
          {CATEGORY_ORDER.map((catKey) => {
            const cat = DRAWING_CATEGORIES[catKey];
            const isSelected = category === catKey;
            const countStaged = getStagedCount(catKey);
            const existingVersion = projectDrawings?.latest[catKey]?.version;

            return (
              <button
                key={catKey}
                type="button"
                onClick={() => setCategory(catKey)}
                style={{
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: isSelected ? `2px solid ${cat.color}` : '1px solid var(--border)',
                  background: isSelected ? cat.bg : 'var(--surface)',
                  color: isSelected ? cat.color : 'var(--text)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  transition: 'all 0.15s ease',
                  boxShadow: isSelected ? `0 2px 8px ${cat.color}22` : 'none',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{cat.label}</div>
                {countStaged > 0 ? (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 12,
                      background: cat.color,
                      color: '#ffffff',
                      fontWeight: 700,
                    }}
                  >
                    +{countStaged} ready
                  </span>
                ) : existingVersion ? (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 12,
                      background: isSelected ? `${cat.color}22` : 'var(--surface-2)',
                      color: isSelected ? cat.color : 'var(--text-soft)',
                      fontWeight: 700,
                    }}
                  >
                    v{existingVersion}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--text-faint)',
                      fontStyle: 'italic',
                    }}
                  >
                    Empty
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Existing Revision info or description */}
        {currentLatest ? (
          <div
            style={{
              marginTop: 10,
              padding: '10px 14px',
              borderRadius: 'var(--radius-sm)',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <CheckCircle2 size={16} style={{ flexShrink: 0, color: '#2563eb' }} />
              <div style={{ fontSize: 12, color: '#1e40af', lineHeight: 1.4 }}>
                Active in database: <strong>v{currentLatest.version}</strong> —{' '}
                <span style={{ fontWeight: 600 }}>{currentLatest.title}</span> ({currentLatest.fileName},{' '}
                {formatFileSize(currentLatest.fileSize)})
              </div>
            </div>

            {admin && (
              <button
                type="button"
                className="btn btn-auto btn-sm"
                onClick={() => setDeleteTarget(currentLatest)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  color: '#dc2626',
                  fontSize: 12,
                  padding: '4px 10px',
                  fontWeight: 600,
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  borderRadius: 'var(--radius-xs)',
                  cursor: 'pointer',
                }}
                title="Delete this active drawing from database"
              >
                <Trash2 size={13} />
                <span>Delete from DB</span>
              </button>
            )}
          </div>
        ) : (
          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              color: 'var(--text-soft)',
            }}
          >
            {activeCategoryMeta.description}
          </div>
        )}
      </div>

      {/* 2. File Dropzone Area (Always visible on top) */}
      <div style={{ marginBottom: 18 }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,application/pdf"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? activeCategoryMeta.color : 'var(--border)'}`,
            borderRadius: 'var(--radius)',
            background: isDragging ? activeCategoryMeta.bg : 'var(--surface-2)',
            padding: '28px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--surface)',
              color: activeCategoryMeta.color,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 10,
              boxShadow: 'var(--shadow-xs)',
            }}
          >
            <UploadCloud size={24} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>
            Click to choose or drag & drop multiple {activeCategoryMeta.label} files
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)', maxWidth: 460, margin: '0 auto' }}>
            Supports drawings and architectural plans (JPG, PNG, WEBP images, and CAD/PDF documents)
          </div>
        </div>
      </div>

      {/* 3. Staged Files List for Current Category */}
      {currentCategoryFiles.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <label className="field-label" style={{ fontWeight: 600, margin: 0 }}>
              Files Ready for Upload ({currentCategoryFiles.length}):
            </label>
            <button
              type="button"
              className="btn btn-auto btn-sm btn-ghost"
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 12,
                color: activeCategoryMeta.color,
                fontWeight: 600,
              }}
            >
              <Plus size={13} />
              <span>Add More Files</span>
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              maxHeight: 280,
              overflowY: 'auto',
              paddingRight: 4,
            }}
          >
            {currentCategoryFiles.map((staged, idx) => {
              const isImage =
                staged.file.type.startsWith('image/') || staged.previewUrl.startsWith('data:image/');
              const isPdf =
                staged.file.type === 'application/pdf' || staged.file.name.toLowerCase().endsWith('.pdf');

              return (
                <div
                  key={staged.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface)',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  {/* Thumbnail / Icon */}
                  {isImage && staged.previewUrl ? (
                    <img
                      src={staged.previewUrl}
                      alt="Preview"
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 'var(--radius-xs)',
                        objectFit: 'cover',
                        border: '1px solid var(--border)',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 'var(--radius-xs)',
                        background: isPdf ? '#fee2e2' : 'var(--surface-2)',
                        color: isPdf ? '#dc2626' : 'var(--text-soft)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {isPdf ? <FileText size={24} /> : <File size={24} />}
                    </div>
                  )}

                  {/* Title & Metadata input */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: activeCategoryMeta.color,
                          background: activeCategoryMeta.bg,
                          border: `1px solid ${activeCategoryMeta.borderColor}`,
                          padding: '1px 6px',
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                      >
                        #{idx + 1}
                      </span>
                      <input
                        type="text"
                        value={staged.title}
                        onChange={(e) => updateStagedTitle(staged.id, e.target.value)}
                        style={{
                          flex: 1,
                          fontSize: 13,
                          padding: '4px 8px',
                          height: 28,
                          borderRadius: 'var(--radius-xs)',
                          border: '1px solid var(--border)',
                          background: 'var(--surface-2)',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-soft)' }}>
                      {staged.file.name} • {formatFileSize(staged.file.size)}
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    className="icon-btn btn-danger"
                    onClick={() => removeStagedFile(staged.id)}
                    title="Remove this file"
                    style={{ width: 28, height: 28, flexShrink: 0 }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. Bottom Staging Summary */}
      {totalStagedCount > 0 && (
        <div
          style={{
            marginTop: 10,
            padding: '10px 14px',
            borderRadius: 'var(--radius-sm)',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
            Ready to upload: <strong>{totalStagedCount} file{totalStagedCount > 1 ? 's' : ''}</strong>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {CATEGORY_ORDER.map((c) => {
              const cnt = getStagedCount(c);
              if (cnt === 0) return null;
              const meta = DRAWING_CATEGORIES[c];
              return (
                <span
                  key={c}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: meta.bg,
                    color: meta.color,
                    border: `1px solid ${meta.borderColor}`,
                  }}
                >
                  {cnt} {meta.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Database Deletion Confirmation */}
      {deleteTarget && (
        <Confirm
          title={`Delete ${DRAWING_CATEGORIES[deleteTarget.category]?.label || 'Drawing'} from Database?`}
          message={`Are you sure you want to delete revision v${deleteTarget.version} (${deleteTarget.fileName}) from the database? If earlier revisions exist, the newest one will become active.`}
          busy={deleteMutation.isPending}
          onConfirm={async () => {
            try {
              await deleteMutation.mutateAsync({
                id: deleteTarget.id,
                category: deleteTarget.category,
                targetProjectId: projectId,
              });
              toast.show(`Drawing deleted from database`);
              setDeleteTarget(null);
            } catch (err) {
              toast.show(err instanceof Error ? err.message : 'Failed to delete drawing from database', true);
            }
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </Drawer>
  );
}

